import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2, Plus, Trash2, FileDown, Save, MessageCircle, AlertTriangle, ArrowLeft, FolderInput } from 'lucide-react';
import api, { API } from '@/lib/api';
import { formatRp, KATEGORI_LABEL, waLink, SATUAN_OPTIONS } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const ORDER = ['profil', 'kaca', 'aksesoris', 'ongkos'];

// Stable client-side id so editable line-item rows keep correct React identity on delete/reorder.
const genUid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const withUid = (items) => (items || []).map((li) => ({ ...li, _uid: li._uid || genUid() }));

export default function PenawaranBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEdit = !!id;

  const [customers, setCustomers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [meta, setMeta] = useState({ customer_id: '', customer_name: '', nama_proyek: '', alamat_proyek: '', dp_pct: 50, valid_until: '', catatan: '', status: 'Draft' });
  const [lineItems, setLineItems] = useState([]);
  const [unpriced, setUnpriced] = useState([]);
  const [marginPct, setMarginPct] = useState(15);
  const [ppnEnabled, setPpnEnabled] = useState(false);
  const [ppnPct, setPpnPct] = useState(11);
  const [savedId, setSavedId] = useState(id || null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiText, setAiText] = useState('');
  const [aiForm, setAiForm] = useState({ jenis_produk: '', lebar_m: '', tinggi_m: '', jenis_kaca: '', warna: '', jumlah_unit: 1, catatan_tambahan: '' });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cs, cat, st] = await Promise.all([api.get('/customers'), api.get('/catalog'), api.get('/settings')]);
      setCustomers(cs.data); setCatalog(cat.data);
      setMarginPct(st.data.default_margin_pct ?? 15);
      setPpnEnabled(!!st.data.ppn_enabled);
      setPpnPct(st.data.ppn_rate ?? 11);
      setMeta((m) => ({ ...m, dp_pct: st.data.default_dp_pct ?? 50 }));
      if (isEdit) {
        const q = await api.get(`/quotes/${id}`);
        const d = q.data;
        setMeta({ customer_id: d.customer_id || '', customer_name: d.customer_name || '', nama_proyek: d.nama_proyek || '', alamat_proyek: d.alamat_proyek || '', dp_pct: d.dp_pct ?? 50, valid_until: d.valid_until || '', catatan: d.catatan || '', status: d.status || 'Draft' });
        setLineItems(withUid(d.line_items));
        setUnpriced(d.unpriced_items || []);
        setMarginPct(d.margin_pct ?? 15);
        setPpnEnabled(!!d.ppn_enabled);
        setPpnPct(d.ppn_pct || 11);
        setSavedId(id);
      }
    } finally { setLoading(false); }
  }, [id, isEdit]);
  useEffect(() => { load(); }, [load]);

  const subtotal = useMemo(() => lineItems.reduce((a, li) => a + (Number(li.qty) || 0) * (Number(li.harga_satuan) || 0), 0), [lineItems]);
  const marginAmount = subtotal * (Number(marginPct) || 0) / 100;
  const dpp = subtotal + marginAmount;
  const ppnAmount = ppnEnabled ? dpp * (Number(ppnPct) || 0) / 100 : 0;
  const grandTotal = dpp + ppnAmount;

  const onSelectCustomer = (cid) => {
    const c = customers.find((x) => x.id === cid);
    setMeta((m) => ({ ...m, customer_id: cid, customer_name: c?.nama || m.customer_name, alamat_proyek: m.alamat_proyek || c?.alamat || '' }));
  };

  const generateAI = async (mode) => {
    setAiLoading(true);
    setUnpriced([]);
    try {
      const payload = { customer_id: meta.customer_id || null, customer_name: meta.customer_name };
      if (mode === 'text') {
        if (!aiText.trim()) { toast.error('Tulis deskripsi pekerjaan dulu'); setAiLoading(false); return; }
        payload.deskripsi = aiText;
      } else {
        if (!aiForm.jenis_produk) { toast.error('Pilih jenis produk dulu'); setAiLoading(false); return; }
        Object.assign(payload, {
          jenis_produk: aiForm.jenis_produk, lebar_m: Number(aiForm.lebar_m) || null, tinggi_m: Number(aiForm.tinggi_m) || null,
          jenis_kaca: aiForm.jenis_kaca, warna: aiForm.warna, jumlah_unit: Number(aiForm.jumlah_unit) || 1, catatan_tambahan: aiForm.catatan_tambahan,
        });
      }
      const r = await api.post('/ai/quote', payload);
      const d = r.data;
      setLineItems(withUid(d.line_items));
      setUnpriced(d.unpriced_items || []);
      if (d.margin_pct != null) setMarginPct(d.margin_pct);
      setMeta((m) => ({ ...m, nama_proyek: m.nama_proyek || d.nama_proyek || 'Penawaran AI' }));
      toast.success(`AI membuat ${d.line_items?.length || 0} item RAB`);
    } catch (e) {
      !e?.__gated && toast.error(e?.response?.data?.detail || 'AI gagal membuat RAB');
    } finally { setAiLoading(false); }
  };

  const updateItem = (idx, field, value) => {
    setLineItems((arr) => arr.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };
  const removeItem = (idx) => setLineItems((arr) => arr.filter((_, i) => i !== idx));
  const addFromCatalog = (c) => {
    setLineItems((arr) => [...arr, { _uid: genUid(), catalog_id: c.id, kategori: c.kategori, nama: c.nama, qty: 1, satuan: c.satuan, harga_satuan: c.harga, subtotal: c.harga }]);
    setPickerOpen(false); setPickerSearch('');
  };

  const buildPayload = (status) => ({
    customer_id: meta.customer_id || null, customer_name: meta.customer_name || '',
    alamat_proyek: meta.alamat_proyek || '', nama_proyek: meta.nama_proyek || 'Penawaran Baru',
    line_items: lineItems.map((li) => ({ catalog_id: li.catalog_id || null, kategori: li.kategori || 'aksesoris', nama: li.nama, qty: Number(li.qty) || 0, satuan: li.satuan || 'pcs', harga_satuan: Number(li.harga_satuan) || 0, subtotal: (Number(li.qty) || 0) * (Number(li.harga_satuan) || 0) })),
    unpriced_items: unpriced,
    margin_pct: Number(marginPct) || 0, ppn_enabled: ppnEnabled, ppn_pct: Number(ppnPct) || 0,
    dp_pct: Number(meta.dp_pct) || 0, valid_until: meta.valid_until || null, catatan: meta.catatan || '',
    status: status || meta.status,
  });

  const save = async (newStatus) => {
    if (lineItems.length === 0) { toast.error('Tambahkan minimal 1 item'); return; }
    if (!meta.nama_proyek.trim()) { toast.error('Isi nama proyek'); return; }
    setSaving(true);
    try {
      const payload = buildPayload(newStatus);
      let res;
      if (savedId) res = await api.put(`/quotes/${savedId}`, payload);
      else res = await api.post('/quotes', payload);
      setSavedId(res.data.id);
      setMeta((m) => ({ ...m, status: res.data.status }));
      toast.success('Penawaran disimpan');
      return res.data.id;
    } catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal menyimpan'); } finally { setSaving(false); }
  };

  const exportPdf = async () => {
    const qid = savedId || await save();
    if (qid) window.open(`${API}/quotes/${qid}/pdf`, '_blank');
  };

  const sendWA = async () => {
    const qid = savedId || await save('Terkirim');
    if (!qid) return;
    const cust = customers.find((c) => c.id === meta.customer_id);
    const text = `Halo ${meta.customer_name || ''}, berikut penawaran untuk *${meta.nama_proyek}* sebesar *${formatRp(grandTotal)}*.\n\nDetail RAB (PDF): ${API}/quotes/${qid}/pdf\n\nTerima kasih.`;
    if (cust?.whatsapp) window.open(waLink(cust.whatsapp, text), '_blank');
    else { navigator.clipboard?.writeText(text); toast.info('Nomor WA pelanggan kosong. Teks penawaran disalin ke clipboard.'); }
  };

  const convert = async () => {
    const qid = savedId || await save('Disetujui');
    if (!qid) return;
    try { await api.post(`/quotes/${qid}/convert`); toast.success('Dikonversi menjadi proyek'); navigate('/proyek'); }
    catch (e) { !e?.__gated && toast.error(e?.response?.data?.detail || 'Gagal konversi'); }
  };

  const grouped = ORDER.map((k) => ({ k, items: lineItems.map((li, i) => ({ li, i })).filter((x) => x.li.kategori === k) })).filter((g) => g.items.length);
  const others = lineItems.map((li, i) => ({ li, i })).filter((x) => !ORDER.includes(x.li.kategori));
  if (others.length) grouped.push({ k: 'lainnya', items: others });

  const filteredCatalog = catalog.filter((c) => !pickerSearch || c.nama.toLowerCase().includes(pickerSearch.toLowerCase()));
  const defaultTab = searchParams.get('tab') === 'ai' ? 'ai' : 'ai';

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 pb-28">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate('/penawaran')} className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-muted"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="font-display text-xl font-bold">{isEdit ? 'Edit Penawaran' : 'Buat Penawaran'}</h1>
      </div>

      {/* Customer & project */}
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5"><Label>Pelanggan</Label>
            <Select value={meta.customer_id} onValueChange={onSelectCustomer}>
              <SelectTrigger data-testid="rab-pelanggan-select"><SelectValue placeholder="Pilih pelanggan (opsional)" /></SelectTrigger>
              <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.nama}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nama Proyek *</Label><Input data-testid="rab-nama-proyek" value={meta.nama_proyek} onChange={(e) => setMeta({ ...meta, nama_proyek: e.target.value })} placeholder="cth: Jendela Sliding Rumah" /></div>
        </div>
        <div className="space-y-1.5"><Label>Alamat Proyek</Label><Input value={meta.alamat_proyek} onChange={(e) => setMeta({ ...meta, alamat_proyek: e.target.value })} placeholder="Alamat pemasangan" /></div>
      </div>

      {/* AI generator */}
      <div className="rounded-xl border border-primary/20 bg-primary/[0.03] p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Sparkles className="h-4 w-4" /></span>
          <div><div className="font-display text-sm font-semibold">Buat RAB dengan AI</div><div className="text-xs text-muted-foreground">AI hitung dari Katalog & Harga Anda</div></div>
        </div>
        <Tabs defaultValue={defaultTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="ai" data-testid="rab-tab-ai">Tulis Bebas</TabsTrigger>
            <TabsTrigger value="form" data-testid="rab-tab-form">Form Cepat</TabsTrigger>
          </TabsList>
          <TabsContent value="ai" className="mt-3 space-y-3">
            <Textarea data-testid="rab-ai-deskripsi" value={aiText} onChange={(e) => setAiText(e.target.value)} rows={3}
              placeholder="cth: Jendela sliding 2 panel, ukuran 1.5m x 1.2m, kaca polos 5mm, warna coklat, 3 unit" />
            <Button data-testid="rab-ai-generate" onClick={() => generateAI('text')} disabled={aiLoading} className="rounded-xl">
              {aiLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Buat RAB
            </Button>
          </TabsContent>
          <TabsContent value="form" className="mt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5 col-span-2"><Label>Jenis Produk</Label><Input value={aiForm.jenis_produk} onChange={(e) => setAiForm({ ...aiForm, jenis_produk: e.target.value })} placeholder="Jendela Sliding / Pintu / Partisi" /></div>
              <div className="space-y-1.5"><Label>Lebar (m)</Label><Input type="number" value={aiForm.lebar_m} onChange={(e) => setAiForm({ ...aiForm, lebar_m: e.target.value })} placeholder="1.5" /></div>
              <div className="space-y-1.5"><Label>Tinggi (m)</Label><Input type="number" value={aiForm.tinggi_m} onChange={(e) => setAiForm({ ...aiForm, tinggi_m: e.target.value })} placeholder="1.2" /></div>
              <div className="space-y-1.5"><Label>Jenis Kaca</Label><Input value={aiForm.jenis_kaca} onChange={(e) => setAiForm({ ...aiForm, jenis_kaca: e.target.value })} placeholder="polos 5mm" /></div>
              <div className="space-y-1.5"><Label>Warna</Label><Input value={aiForm.warna} onChange={(e) => setAiForm({ ...aiForm, warna: e.target.value })} placeholder="Coklat" /></div>
              <div className="space-y-1.5"><Label>Jumlah Unit</Label><Input type="number" value={aiForm.jumlah_unit} onChange={(e) => setAiForm({ ...aiForm, jumlah_unit: e.target.value })} placeholder="1" /></div>
              <div className="space-y-1.5"><Label>Catatan</Label><Input value={aiForm.catatan_tambahan} onChange={(e) => setAiForm({ ...aiForm, catatan_tambahan: e.target.value })} placeholder="opsional" /></div>
            </div>
            <Button onClick={() => generateAI('form')} disabled={aiLoading} className="rounded-xl">
              {aiLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Buat RAB
            </Button>
          </TabsContent>
        </Tabs>
      </div>

      {/* Unpriced warning */}
      {unpriced.length > 0 && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-orange-800"><AlertTriangle className="h-4 w-4" /> Item belum ada harganya di katalog</div>
          <ul className="mt-2 space-y-1 text-sm text-orange-900">
            {unpriced.map((u, i) => <li key={`${u.nama}-${i}`}>• <b>{u.nama}</b>{u.alasan ? ` — ${u.alasan}` : ''}</li>)}
          </ul>
          <p className="mt-2 text-xs text-orange-700">Tambahkan item ini di Katalog & Harga, lalu buat ulang, atau tambah manual.</p>
        </div>
      )}

      {/* Line items editor */}
      <div className="rounded-xl border bg-card p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-sm font-semibold">Rincian Item</h2>
          <Button data-testid="rab-tambah-item-button" variant="outline" size="sm" onClick={() => setPickerOpen(true)} className="rounded-lg"><Plus className="mr-1 h-4 w-4" /> Tambah Item</Button>
        </div>
        {lineItems.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Belum ada item. Buat dengan AI atau tambah manual dari katalog.</p>
        ) : (
          <div className="space-y-4">
            {grouped.map((g) => (
              <div key={g.k}>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">{KATEGORI_LABEL[g.k] || 'Lainnya'}</div>
                <div className="space-y-2">
                  {g.items.map(({ li, i }) => (
                    <div key={li._uid || i} className="rounded-lg border bg-background p-3">
                      <div className="flex items-start gap-2">
                        <Input value={li.nama} onChange={(e) => updateItem(i, 'nama', e.target.value)} className="h-9 flex-1 font-medium" />
                        <button onClick={() => removeItem(i)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
                      </div>
                      <div className="mt-2 grid grid-cols-12 gap-2">
                        <div className="col-span-3"><Label className="text-[10px] text-muted-foreground">Qty</Label><Input type="number" value={li.qty} onChange={(e) => updateItem(i, 'qty', e.target.value)} className="h-9" /></div>
                        <div className="col-span-3"><Label className="text-[10px] text-muted-foreground">Satuan</Label>
                          <Select value={li.satuan} onValueChange={(v) => updateItem(i, 'satuan', v)}>
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>{SATUAN_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-6"><Label className="text-[10px] text-muted-foreground">Harga Satuan</Label><Input type="number" value={li.harga_satuan} onChange={(e) => updateItem(i, 'harga_satuan', e.target.value)} className="h-9" /></div>
                      </div>
                      <div className="mt-1.5 text-right text-sm font-semibold tabular-nums">{formatRp((Number(li.qty) || 0) * (Number(li.harga_satuan) || 0))}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pricing settings + totals */}
      <div className="rounded-xl border bg-card p-4 space-y-3">
        <h2 className="font-display text-sm font-semibold">Perhitungan</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Margin (%)</Label><Input data-testid="rab-margin-input" type="number" value={marginPct} onChange={(e) => setMarginPct(e.target.value)} className="h-9" /></div>
          <div className="space-y-1.5"><Label>DP (%)</Label><Input type="number" value={meta.dp_pct} onChange={(e) => setMeta({ ...meta, dp_pct: e.target.value })} className="h-9" /></div>
        </div>
        <div className="flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm font-medium">PPN {ppnEnabled ? `(${ppnPct}%)` : ''}</span>
          <Switch data-testid="rab-ppn-toggle" checked={ppnEnabled} onCheckedChange={setPpnEnabled} />
        </div>
        <div className="space-y-1.5 border-t pt-3 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="tabular-nums">{formatRp(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-muted-foreground">Margin ({marginPct}%)</span><span className="tabular-nums">{formatRp(marginAmount)}</span></div>
          {ppnEnabled && <div className="flex justify-between"><span className="text-muted-foreground">PPN ({ppnPct}%)</span><span className="tabular-nums">{formatRp(ppnAmount)}</span></div>}
          <div className="flex justify-between border-t pt-2 text-base font-bold"><span>Total</span><span data-testid="rab-grand-total" className="tabular-nums text-primary">{formatRp(grandTotal)}</span></div>
        </div>
      </div>

      {/* Sticky actions */}
      <div className="fixed bottom-16 left-0 right-0 z-20 border-t bg-card/95 p-3 backdrop-blur md:bottom-0 md:left-64">
        <div className="mx-auto flex max-w-6xl flex-wrap gap-2">
          <Button data-testid="rab-simpan-button" onClick={() => save()} disabled={saving} className="flex-1 rounded-xl md:flex-none">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Simpan
          </Button>
          <Button data-testid="rab-export-pdf-button" onClick={exportPdf} variant="outline" className="rounded-xl"><FileDown className="mr-2 h-4 w-4" /> PDF</Button>
          <Button data-testid="rab-kirim-whatsapp-button" onClick={sendWA} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90"><MessageCircle className="mr-2 h-4 w-4" /> WA</Button>
          <Button onClick={convert} variant="outline" className="rounded-xl"><FolderInput className="mr-2 h-4 w-4" /> Jadikan Proyek</Button>
        </div>
      </div>

      {/* Catalog picker */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader><DialogTitle className="font-display">Pilih Item dari Katalog</DialogTitle></DialogHeader>
          <Input value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} placeholder="Cari item..." className="mb-2" />
          <div className="flex-1 overflow-y-auto space-y-1.5">
            {filteredCatalog.map((c) => (
              <button key={c.id} onClick={() => addFromCatalog(c)} className="flex w-full items-center justify-between rounded-lg border bg-background p-2.5 text-left hover:bg-muted">
                <div className="min-w-0"><div className="truncate text-sm font-medium">{c.nama}</div><div className="text-xs text-muted-foreground">{KATEGORI_LABEL[c.kategori]}</div></div>
                <div className="text-right text-sm"><div className="font-semibold tabular-nums">{formatRp(c.harga)}</div><div className="text-xs text-muted-foreground">/{c.satuan}</div></div>
              </button>
            ))}
            {filteredCatalog.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">Tidak ada item.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
